import { useEffect, useMemo, useRef } from 'react';
import { Upload } from 'lucide-react';
import type { DocumentFolder, UploadLinkConfig } from '@peles/shared';
import { UPLOAD_FILE_TYPES } from '@peles/shared';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useDocumentFolders } from '../../hooks/useDocumentFolders';

/**
 * "Ask for a file back", with somewhere to put it and something to expect
 * (COMMUNICATIONS-DESIGN C7).
 *
 * ⚠️ **Built once for every composer, not per feature.** The vendor email had
 * this as a bare checkbox and nothing else could reach it — which is why the
 * feature existed for months without being findable. It drops into
 * `ComposeEmailModal`'s `extraOptions` slot, so any email gains it by passing
 * this rather than by growing its own.
 *
 * ⚠️ **A folder is not required.** An email about a record can attach the file
 * to that record and file it nowhere in particular, exactly as before; a folder
 * only adds where it lands. What is required is that at least *one* of the two
 * exists, and the caller — not this component — owns the record half, so the
 * server refuses the empty case rather than this trying to.
 */
export interface UploadLinkOptionsProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  config: UploadLinkConfig;
  onConfigChange: (config: UploadLinkConfig) => void;
  /** True when the email is about a record, so a folder is genuinely optional. */
  hasRecordTarget?: boolean;
  /**
   * The record this email is about, when there is one.
   *
   * ⚠️ **It narrows the folder list to that record's own folder and what is
   * under it.** An email sent from an event used to offer every folder in the
   * application — a hundred other events' folders, each named after somebody
   * else's family. That is a worse answer than no picker at all: the right
   * folder is hard to find and a wrong one is one keystroke away, filing a
   * vendor's contract under an unrelated client.
   *
   * It also seeds the default, because "with this event's files" is what the
   * sender meant by sending from the event.
   */
  scope?: { entityType: string; entityId: string };
  disabled?: boolean;
}

/** "Events / Smith Wedding" — a name alone is ambiguous across branches. */
export function folderPaths(folders: DocumentFolder[]): { id: string; path: string }[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));

  function pathOf(folder: DocumentFolder, seen: Set<string>): string {
    // `seen` guards a parent cycle: the tree should not have one, but a render
    // that hangs is a much worse failure than a slightly wrong label.
    if (!folder.parentId || seen.has(folder.parentId)) return folder.name;
    const parent = byId.get(folder.parentId);
    if (!parent) return folder.name;
    seen.add(folder.parentId);
    return `${pathOf(parent, seen)} / ${folder.name}`;
  }

  return folders
    .map((folder) => ({ id: folder.id, path: pathOf(folder, new Set([folder.id])) }))
    .sort((a, b) => a.path.localeCompare(b.path));
}

/**
 * The folder anchored to a record, and everything beneath it.
 *
 * Walked downwards from the anchor rather than upwards from every folder: a
 * subtree is the answer, and asking each folder "is one of my ancestors this
 * event?" re-walks the same chain once per folder. `seen` is what stops a
 * parent cycle turning that walk into a hang — the same guard `folderPaths`
 * carries, for the same reason.
 */
export function folderSubtree(
  folders: DocumentFolder[],
  scope: { entityType: string; entityId: string },
): DocumentFolder[] {
  const anchor = folders.find(
    (folder) => folder.entityType === scope.entityType && folder.entityId === scope.entityId,
  );
  if (!anchor) return [];

  const childrenOf = new Map<string, DocumentFolder[]>();
  for (const folder of folders) {
    if (!folder.parentId) continue;
    const siblings = childrenOf.get(folder.parentId) ?? [];
    siblings.push(folder);
    childrenOf.set(folder.parentId, siblings);
  }

  const out: DocumentFolder[] = [];
  const seen = new Set<string>();
  const queue = [anchor];
  while (queue.length > 0) {
    const folder = queue.shift()!;
    if (seen.has(folder.id)) continue;
    seen.add(folder.id);
    out.push(folder);
    queue.push(...(childrenOf.get(folder.id) ?? []));
  }
  return out;
}

/**
 * The destination and the accepted types, without the checkbox in front of them.
 *
 * ⚠️ **Extracted so a second caller cannot re-implement the seeding rule.** The
 * "which folder" question is the same wherever it is asked, and the interesting
 * part is not the input — it is the note below about re-seeding when the record
 * changes, which took a bug to find. A conversation asking for a file back
 * needs those fields and does *not* need a checkbox saying whether to ask,
 * because a dialog called "Ask for a file back" has already answered that. A
 * copy of these two inputs there would have been a copy of the seeding rule
 * too, and the copy is what goes stale.
 *
 * ⚠️ **Mounting is the arming.** `UploadLinkOptions` renders this only when its
 * checkbox is on, so unmounting resets the "which record did I seed for" ref
 * exactly as switching the option off used to — one mechanism rather than a
 * flag and a lifecycle saying the same thing.
 */
export interface UploadLinkFieldsProps {
  config: UploadLinkConfig;
  onConfigChange: (config: UploadLinkConfig) => void;
  hasRecordTarget?: boolean;
  scope?: { entityType: string; entityId: string };
  /**
   * The folders to offer, when the caller already knows them.
   *
   * ⚠️ **Because there are two file systems and only one of them is a
   * conversation's.** A vendor email files into `document_folders`, which is
   * what the walk below lists and what `finalizeUpload` reads. A conversation
   * files into the `file-client` library, whose folders are nodes and whose ids
   * are not uuids — and pointing this component at the wrong one is exactly what
   * made "File it in" answer "No options" on every conversation. So the
   * conversation supplies its own list, resolved server-side by the endpoint
   * that also validates the choice, and the email path is left alone.
   *
   * Ordered root-first — the caller's first entry is the record's own folder —
   * but *not* seeded as the default the way the email's anchor is: a
   * conversation that picks nothing files into its record's `Conversations`
   * folder, which is where its files belong.
   */
  folders?: { id: string; path: string }[];
  /**
   * Whether that list is still being fetched.
   *
   * Only the empty-state line depends on it: an empty array while a walk is in
   * flight is not "this record has no folders yet", and saying so and then
   * filling the dropdown a moment later is worse than saying nothing.
   */
  foldersLoading?: boolean;
  disabled?: boolean;
}

export function UploadLinkFields({
  config,
  onConfigChange,
  hasRecordTarget = false,
  scope,
  folders: supplied,
  foldersLoading = false,
  disabled = false,
}: UploadLinkFieldsProps) {
  // One request for the whole tree: the endpoint returns every folder when no
  // parent is named, and paths are cheaper to build here than to walk over the
  // wire one level at a time.
  //
  // Skipped entirely when the caller supplied its own list — a conversation has
  // no use for `document_folders` and no reason to fetch all of them.
  const { data: folders = [] } = useDocumentFolders({}, supplied === undefined);
  // Depending on the two strings rather than the object: callers write
  // `scope={{ ... }}` inline, so the object is new on every render and the walk
  // would re-run on every keystroke in the message body above it.
  const scopeType = scope?.entityType;
  const scopeId = scope?.entityId;
  const inScope = useMemo(
    () =>
      scopeType !== undefined && scopeId !== undefined
        ? folderSubtree(folders, { entityType: scopeType, entityId: scopeId })
        : folders,
    [folders, scopeType, scopeId],
  );
  const options = useMemo(() => supplied ?? folderPaths(inScope), [supplied, inScope]);

  // The record's own folder — the anchor is always first out of either walk — as
  // the default when the sender has not chosen otherwise. Seeded through
  // `onConfigChange` rather than read as a fallback at render, so what is shown
  // and what is submitted are one value: a default living only in the input is
  // one the server never receives.
  /*
   * ⚠️ **Seeded for an email, deliberately not for a conversation.** "With this
   * event's files" is what somebody means by sending from the event, so the
   * email path opens on the record's own folder. A conversation's files have a
   * better home: choosing nothing leaves `folderId` null and the server files
   * into the record's `Conversations` folder, which exists precisely so a
   * transcript's reports and uploads do not land loose among the contracts —
   * see `conversation-folder.ts`. Seeding the record's node here would have
   * quietly undone that for everyone who never opened the dropdown.
   *
   * The folder is still offered; it is only no longer chosen on the sender's
   * behalf.
   */
  const anchorId =
    supplied !== undefined ? null : scopeType !== undefined ? (inScope[0]?.id ?? null) : null;

  /*
   * ⚠️ **Re-seeded when the record changes, not only when the option opens.**
   * The guard used to be "a folder is already chosen, leave it alone", which
   * is right within one email and wrong across two: open the composer on one
   * event, switch to another, and the first event's folder is still in the
   * form and still submitted. Remembering *which* record was seeded is what
   * tells "the sender picked this" apart from "the last record left this".
   *
   * The server drops a folder outside the event regardless — a UI narrowing is
   * a convenience and never a control — but a form showing one event's folder
   * while addressing another is a lie to the sender even when the outcome is
   * safe.
   */
  const seededFor = useRef<string | null>(null);
  const scopeKey = scopeType === undefined ? null : `${scopeType}:${scopeId ?? ''}`;
  useEffect(() => {
    if (anchorId === null || seededFor.current === scopeKey) return;
    // A folder chosen under *this* record is the sender's and is left alone; a
    // folder left over from another one is replaced.
    if (seededFor.current === null && config.folderId) {
      seededFor.current = scopeKey;
      return;
    }
    seededFor.current = scopeKey;
    onConfigChange({ ...config, folderId: anchorId });
  }, [anchorId, scopeKey, config, onConfigChange]);

  return (
    <div className="space-y-3" data-testid="upload-link-config">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          File it in{hasRecordTarget ? ' (optional)' : ''}
        </label>
        <SearchableListSelect
          items={options}
          value={config.folderId ?? null}
          onChange={(value) =>
            onConfigChange({
              ...config,
              folderId: typeof value === 'string' && value ? value : null,
            })
          }
          getValue={(option) => option.id}
          getLabel={(option) => option.path}
          placeholder={
            // Naming where it actually goes, rather than leaving a conversation's
            // sender to guess what "the record only" means for a file.
            supplied !== undefined
              ? 'The conversation’s own folder'
              : hasRecordTarget
                ? 'Attach to the record only'
                : 'Choose a folder…'
          }
          disabled={disabled}
        />
        {/*
          Said out loud, because "No options" inside a closed dropdown is not an
          explanation — it was the entire visible symptom of the picker being
          pointed at the wrong file system.
        */}
        {(scopeType !== undefined || supplied !== undefined) &&
          options.length === 0 &&
          !foldersLoading && (
            <p className="mt-1 text-xs text-gray-500" data-testid="no-scoped-folders">
              This record has no folders yet.
            </p>
          )}
        {!hasRecordTarget && !config.folderId && (
          <p className="mt-1 text-xs text-amber-600" data-testid="folder-required">
            Choose a folder so the file has somewhere to go.
          </p>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Accept (leave empty for any file)
        </label>
        <SearchableListSelect
          items={[...UPLOAD_FILE_TYPES]}
          value={config.fileTypes ?? []}
          onChange={(value) =>
            onConfigChange({ ...config, fileTypes: Array.isArray(value) ? value : [value] })
          }
          getValue={(type) => type.key}
          getLabel={(type) => type.label}
          getSubtitle={(type) => type.extensions.join(' ')}
          placeholder="Any file"
          multiple
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export function UploadLinkOptions({
  enabled,
  onEnabledChange,
  config,
  onConfigChange,
  hasRecordTarget = false,
  scope,
  disabled = false,
}: UploadLinkOptionsProps) {
  return (
    <div className="space-y-3">
      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          data-testid="include-upload-link"
        />
        <span>
          <span className="flex items-center gap-1 font-medium">
            <Upload size={13} />
            Ask for a file back
          </span>
          <span className="text-xs text-gray-500">
            Adds a button each recipient can use to send you a file — no login needed.
          </span>
        </span>
      </label>

      {enabled && (
        <div className="pl-6">
          <UploadLinkFields
            config={config}
            onConfigChange={onConfigChange}
            hasRecordTarget={hasRecordTarget}
            {...(scope ? { scope } : {})}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}
